import React, { useState, useEffect, useCallback } from 'react';
import Layout from '@/components/layout/Layout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from '@/components/ui/tabs';
import {
  ArrowLeft, Search, Loader2, Download, FileText,
  ExternalLink, CheckCircle, X as XIcon, DollarSign,
  Globe, Sparkles, RefreshCw, ChevronDown, ChevronUp,
  AlertTriangle, UserPlus,
} from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import api from '@/services/api';
import InfoTip from '@/components/InfoTip';
import { useCurrency } from '@/context/CurrencyContext';
import { useBranding } from '@/context/BrandingContext';
import { createBrandedPDF, addSectionTitle, addMetricCards, addTable, downloadPDF, addText } from '@/utils/pdfExport';
import QuickAssignModal from '@/components/QuickAssignModal';

const fmt = (v) => {
  if (v == null || v === '' || isNaN(v) || v === 0) return '-';
  return `$${Number(v).toFixed(4)}`;
};

const priorityColor = {
  critical: 'bg-red-100 text-red-800 border-red-200',
  high: 'bg-orange-100 text-orange-800 border-orange-200',
  medium: 'bg-yellow-100 text-yellow-800 border-yellow-200',
};

const categoryColor = {
  negotiation: 'bg-blue-100 text-blue-800',
  consolidation: 'bg-purple-100 text-purple-800',
  supplier_strategy: 'bg-teal-100 text-teal-800',
  supplier_comparison: 'bg-indigo-100 text-indigo-800',
  savings: 'bg-green-100 text-green-800',
  market_timing: 'bg-amber-100 text-amber-800',
  cherry_pick: 'bg-pink-100 text-pink-800',
};

const SUPPLIER_COLORS = [
  { bg: 'bg-violet-50', border: 'border-violet-200', text: 'text-violet-700', head: 'text-violet-700' },
  { bg: 'bg-cyan-50', border: 'border-cyan-200', text: 'text-cyan-700', head: 'text-cyan-700' },
  { bg: 'bg-rose-50', border: 'border-rose-200', text: 'text-rose-700', head: 'text-rose-700' },
];

const OneBidDetail = () => {
  const { bidId } = useParams();
  const navigate = useNavigate();
  const { format } = useCurrency();
  const { branding } = useBranding();

  const [bid, setBid] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('general');
  const [expandedMpn, setExpandedMpn] = useState(null);
  const [recommendations, setRecommendations] = useState(null);
  const [recoLoading, setRecoLoading] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignCtx, setAssignCtx] = useState({});

  const fetchBid = useCallback(async () => {
    try {
      const res = await api.get(`/bids/${bidId}`);
      setBid(res.data);
    } catch {
      toast.error('Failed to load bid');
    }
    setLoading(false);
  }, [bidId]);

  useEffect(() => { fetchBid(); }, [fetchBid]);

  useEffect(() => {
    if (!bid || bid.status !== 'processing') return;
    const interval = setInterval(async () => {
      try {
        const res = await api.get(`/bids/${bidId}/status`);
        if (res.data.status === 'completed') { clearInterval(interval); fetchBid(); }
      } catch { /* ignore */ }
    }, 2000);
    return () => clearInterval(interval);
  }, [bid, bidId, fetchBid]);

  const fetchRecommendations = useCallback(async (regen = false) => {
    setRecoLoading(true);
    try {
      const res = await api.get(`/bids/${bidId}/recommendations${regen ? '?regenerate=true' : ''}`);
      setRecommendations(res.data);
    } catch { toast.error('Failed to load recommendations'); }
    setRecoLoading(false);
  }, [bidId]);

  const results = bid?.results || [];
  const summary = bid?.summary || {};
  const supplierNames = summary.supplier_names || bid?.supplier_names || [];
  const numBids = summary.num_bids || supplierNames.length || 1;

  const filtered = results.filter(r => {
    if (!searchTerm) return true;
    const s = searchTerm.toLowerCase();
    return r.mpn?.toLowerCase().includes(s) || r.cpn?.toLowerCase().includes(s) || r.make?.toLowerCase().includes(s) || r.description?.toLowerCase().includes(s);
  });

  const handleCSVExport = async () => {
    try {
      const res = await api.get(`/bids/${bidId}/export/csv`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url; a.download = `bid-analysis-${bidId}.csv`; a.click();
      window.URL.revokeObjectURL(url);
      toast.success('CSV downloaded');
    } catch { toast.error('Export failed'); }
  };

  const handlePDFExport = () => {
    if (!bid || !results.length) return;
    const doc = createBrandedPDF(`Bid Analysis: ${bid.bom_name || bidId}`, 'Bid Validation Report', branding);
    let y = doc._startY;

    y = addSectionTitle(doc, 'BOM Summary', y);
    y = addMetricCards(doc, [
      { label: 'Components', value: summary.total_components || 0 },
      { label: 'Matched', value: summary.matched_count || 0 },
      { label: 'Suppliers', value: supplierNames.join(', ') },
      { label: 'Best Savings', value: `$${(summary.total_best_savings || 0).toFixed(2)}` },
    ], y);
    y += 4;

    y = addSectionTitle(doc, 'Pricing Comparison', y);
    const priceHead = ['MPN', ...supplierNames.map(n => n.substring(0, 12)), 'Prob. Price', 'Best ALL'];
    const priceBody = results.slice(0, 35).map(r => {
      const sp = [];
      for (let i = 0; i < numBids; i++) {
        const p = r.bid_prices?.[String(i)];
        const fl = r.flagged?.[String(i)];
        sp.push(p ? `$${p.toFixed(4)}${fl ? '*' : ''}` : '-');
      }
      return [r.mpn, ...sp, fmt(r.anchor_all), fmt(r.best_price_all)];
    });
    y = addTable(doc, priceHead, priceBody, y);

    // Flagged components
    const flaggedList = results.filter(r => Object.values(r.flagged || {}).some(v => v));
    if (flaggedList.length > 0) {
      y = addSectionTitle(doc, `Flagged Components (${flaggedList.length})`, y);
      y = addTable(doc,
        ['MPN', 'Flagged Supplier(s)', 'Prob. Price ALL', 'Issue'],
        flaggedList.slice(0, 20).map(r => {
          const flaggedSuppliers = Object.entries(r.flagged || {}).filter(([,v]) => v).map(([k]) => supplierNames[parseInt(k)] || `Supplier ${k}`).join(', ');
          return [r.mpn, flaggedSuppliers, fmt(r.anchor_all), 'Bid exceeds market benchmark'];
        }),
        y
      );
    }

    // AI Recommendations
    if (recommendations?.recommendations?.length > 0) {
      y = addSectionTitle(doc, 'AI Recommendations', y);
      if (recommendations.summary) {
        y = addText(doc, recommendations.summary, y);
      }
      y = addTable(doc,
        ['#', 'Priority', 'Category', 'Recommendation', 'Components', 'Savings'],
        recommendations.recommendations.map(r => [
          String(r.id), r.priority?.toUpperCase(), (r.category || '').replace(/_/g, ' '),
          r.title, (r.affected_components || []).slice(0, 3).join(', '), r.estimated_savings || '-',
        ]),
        y
      );
    }

    downloadPDF(doc, `bid-analysis-${bid.bom_name || bidId}.pdf`);
    toast.success('PDF downloaded');
  };

  if (loading) return <Layout title="Bid Analyzer"><div className="flex items-center justify-center py-32"><Loader2 className="h-8 w-8 animate-spin text-blue-500" /></div></Layout>;
  if (!bid) return <Layout title="Bid Analyzer"><div className="text-center py-16 text-gray-500">Bid not found. <Button variant="link" onClick={() => navigate('/1bid')}>Go back</Button></div></Layout>;
  if (bid.status === 'processing') return (
    <Layout title="Bid Analyzer">
      <div className="max-w-3xl mx-auto text-center py-20 space-y-4">
        <Loader2 className="h-12 w-12 text-blue-500 animate-spin mx-auto" />
        <h3 className="text-xl font-bold text-gray-900">Analyzing Bids...</h3>
        <p className="text-sm text-gray-500">Comparing bids across suppliers and against 1Data pricing. This may take a moment.</p>
      </div>
    </Layout>
  );

  const totalFlagged = Object.values(summary.supplier_flagged_counts || {}).reduce((a, b) => a + b, 0);

  return (
    <Layout title="Bid Analyzer" subtitle={bid.bom_name || ''} breadcrumbs={[
      { label: 'Bid Analyzer', path: '/1bid' },
      { label: bid.bom_name || 'Bid Detail' },
    ]}>
      <div className="space-y-5" data-testid="one-bid-detail-page">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-2">
          <Button variant="ghost" size="sm" onClick={() => navigate('/1bid')} className="gap-1 text-gray-600" data-testid="btn-back">
            <ArrowLeft className="h-4 w-4" /> Back to Bids
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" className="gap-1 text-xs" onClick={handleCSVExport} data-testid="btn-export-csv">
              <FileText className="h-3.5 w-3.5" /> CSV
            </Button>
            <Button variant="outline" size="sm" className="gap-1 text-xs" onClick={handlePDFExport} data-testid="btn-export-pdf">
              <Download className="h-3.5 w-3.5" /> PDF
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="summary-cards">
          <Card className="bg-white border-gray-200 shadow-sm">
            <CardContent className="pt-3 pb-2.5">
              <p className="text-[10px] text-gray-500 uppercase">Components</p>
              <p className="text-lg font-bold text-gray-900 mt-0.5">{summary.total_components || 0}</p>
              <p className="text-[10px] text-gray-400">{summary.matched_count || 0} matched</p>
            </CardContent>
          </Card>
          {supplierNames.map((sn, i) => {
            const colors = SUPPLIER_COLORS[i % SUPPLIER_COLORS.length];
            const total = summary.supplier_totals?.[String(i)] || 0;
            const flagged = summary.supplier_flagged_counts?.[String(i)] || 0;
            return (
              <Card key={i} className={`${colors.bg} ${colors.border} shadow-sm`}>
                <CardContent className="pt-3 pb-2.5">
                  <p className={`text-[10px] uppercase ${colors.text}`}>{sn}</p>
                  <p className={`text-lg font-bold ${colors.text} mt-0.5`}>{format(total)}</p>
                  {flagged > 0 && <p className="text-[10px] text-red-500 flex items-center gap-0.5"><AlertTriangle className="h-2.5 w-2.5" /> {flagged} overpriced</p>}
                </CardContent>
              </Card>
            );
          })}
          <Card className="bg-green-50 border-green-200 shadow-sm">
            <CardContent className="pt-3 pb-2.5">
              <p className="text-[10px] text-green-600 uppercase">1Data Best (ALL)</p>
              <p className="text-lg font-bold text-green-700 mt-0.5">{format(summary.total_best_all || 0)}</p>
              <p className="text-[10px] text-green-400">Market lowest</p>
            </CardContent>
          </Card>
          <Card className="bg-emerald-50 border-emerald-200 shadow-sm">
            <CardContent className="pt-3 pb-2.5">
              <p className="text-[10px] text-emerald-600 uppercase flex items-center gap-1">Best Savings <InfoTip text="Cheapest supplier total minus 1Data Best (ALL) total." /></p>
              <p className="text-lg font-bold text-emerald-700 mt-0.5">{format(summary.total_best_savings || 0)}</p>
              <p className="text-[10px] text-emerald-400">Max achievable</p>
            </CardContent>
          </Card>
        </div>

        {/* Flagged warning */}
        {totalFlagged > 0 && (
          <div className="flex items-center gap-2 px-4 py-2 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700" data-testid="flagged-warning">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            <span><strong>{totalFlagged} MPN{totalFlagged > 1 ? 's' : ''}</strong> flagged where supplier price exceeds 1Data&apos;s probabilistic price. Highlighted in red below.</span>
          </div>
        )}

        {/* Search */}
        <div className="relative max-w-xs">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input placeholder="Search MPN, CPN, Make..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-9 h-9 text-sm" data-testid="search-input" />
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-gray-100 h-9">
            <TabsTrigger value="general" className="text-xs gap-1" data-testid="tab-general"><FileText className="h-3.5 w-3.5" /> General</TabsTrigger>
            <TabsTrigger value="price" className="text-xs gap-1" data-testid="tab-price"><DollarSign className="h-3.5 w-3.5" /> Price</TabsTrigger>
            <TabsTrigger value="distributors" className="text-xs gap-1" data-testid="tab-distributors"><Globe className="h-3.5 w-3.5" /> Distributor Pricing</TabsTrigger>
            <TabsTrigger value="ai" className="text-xs gap-1" data-testid="tab-ai"><Sparkles className="h-3.5 w-3.5" /> AI Recommendations</TabsTrigger>
          </TabsList>

          {/* ── GENERAL TAB ── */}
          <TabsContent value="general">
            <Card className="bg-white border-gray-200 shadow-sm">
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-50/80 text-[11px]">
                        <TableHead className="font-semibold">MPN</TableHead>
                        <TableHead className="font-semibold">CPN</TableHead>
                        <TableHead className="font-semibold">Make</TableHead>
                        <TableHead className="font-semibold text-center">Matched?</TableHead>
                        <TableHead className="font-semibold">Brief Description</TableHead>
                        <TableHead className="font-semibold text-center">Datasheet</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filtered.map((r, idx) => (
                        <TableRow key={idx} className="text-xs hover:bg-gray-50" data-testid={`general-row-${idx}`}>
                          <TableCell className="font-mono font-medium text-gray-900">{r.mpn}</TableCell>
                          <TableCell className="text-gray-600">{r.cpn || '-'}</TableCell>
                          <TableCell className="text-gray-600">{r.make || '-'}</TableCell>
                          <TableCell className="text-center">
                            {r.matched ? <CheckCircle className="h-4 w-4 text-green-600 mx-auto" /> : <XIcon className="h-4 w-4 text-red-400 mx-auto" />}
                          </TableCell>
                          <TableCell className="text-gray-600 max-w-[200px] truncate">{r.description || '-'}</TableCell>
                          <TableCell className="text-center">
                            {r.datasheet_url ? <a href={r.datasheet_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline inline-flex items-center gap-0.5 text-[10px]"><ExternalLink className="h-3 w-3" /> View</a> : '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                      {filtered.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-8 text-gray-400 text-sm">No components found</TableCell></TableRow>}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ── PRICE TAB (dynamic supplier columns) ── */}
          <TabsContent value="price">
            <Card className="bg-white border-gray-200 shadow-sm">
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-50/80 text-xs">
                        <TableHead className="font-semibold sticky left-0 bg-gray-50 z-10">MPN</TableHead>
                        <TableHead className="font-semibold">Qty</TableHead>
                        {/* Supplier columns */}
                        {supplierNames.map((sn, i) => {
                          const colors = SUPPLIER_COLORS[i % SUPPLIER_COLORS.length];
                          return <TableHead key={`s-${i}`} className={`font-semibold ${colors.head}`}>{sn}</TableHead>;
                        })}
                        {/* 1Data columns */}
                        <TableHead className="font-semibold text-blue-700">Median (ALL)</TableHead>
                        <TableHead className="font-semibold text-blue-700">Best (ALL)</TableHead>
                        <TableHead className="font-semibold text-blue-700">Prob. Price (ALL)</TableHead>
                        <TableHead className="font-semibold text-amber-700">Best (Dom)</TableHead>
                        <TableHead className="font-semibold text-amber-700">Prob. Price (Dom)</TableHead>
                        <TableHead className="font-semibold text-purple-700">Best (Glb)</TableHead>
                        <TableHead className="font-semibold text-purple-700">Prob. Price (Glb)</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filtered.map((r, idx) => {
                        const probAll = r.anchor_all || 0;
                        return (
                        <TableRow key={idx} className="text-xs hover:bg-gray-50" data-testid={`price-row-${idx}`}>
                          <TableCell className="font-mono font-medium text-gray-900 sticky left-0 bg-white z-10">{r.mpn}</TableCell>
                          <TableCell className="text-gray-500">{r.quantity || '-'}</TableCell>
                          {/* Supplier price cells with graduated highlighting */}
                          {supplierNames.map((sn, i) => {
                            const bp = r.bid_prices?.[String(i)];
                            if (!bp || bp === 0) return <TableCell key={`sp-${i}`} className="text-gray-300">-</TableCell>;
                            const pctAbove = probAll > 0 ? ((bp - probAll) / probAll) * 100 : 0;
                            let cellBg = '';
                            let tooltipText = '';
                            if (pctAbove >= 20) {
                              cellBg = 'bg-red-50';
                              tooltipText = `${pctAbove.toFixed(1)}% above Prob. Price (ALL) — significantly overpriced`;
                            } else if (pctAbove >= 1) {
                              cellBg = 'bg-yellow-50';
                              tooltipText = `${pctAbove.toFixed(1)}% above Prob. Price (ALL)`;
                            }
                            return (
                              <TableCell key={`sp-${i}`} className={`font-semibold ${cellBg}`} data-testid={`sp-cell-${idx}-${i}`}>
                                <span className="flex items-center gap-0.5 group relative">
                                  <span className={pctAbove >= 20 ? 'text-red-700' : pctAbove >= 1 ? 'text-amber-700' : ''}>{fmt(bp)}</span>
                                  {pctAbove >= 1 && <AlertTriangle className={`h-3 w-3 shrink-0 ${pctAbove >= 20 ? 'text-red-500' : 'text-amber-500'}`} />}
                                  {tooltipText && (
                                    <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 px-2.5 py-1.5 bg-gray-900 text-white text-[10px] rounded-md whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-20">
                                      {tooltipText}
                                    </span>
                                  )}
                                </span>
                              </TableCell>
                            );
                          })}
                          {/* 1Data ALL */}
                          <TableCell>{fmt(r.median_price_all)}</TableCell>
                          <TableCell className="text-green-700 font-semibold">{fmt(r.best_price_all)}</TableCell>
                          <TableCell className="text-blue-700">{fmt(r.anchor_all)}</TableCell>
                          {/* 1Data Domestic */}
                          <TableCell className="text-green-700">{fmt(r.best_price_domestic)}</TableCell>
                          <TableCell className="text-amber-700">{fmt(r.anchor_domestic)}</TableCell>
                          {/* 1Data Global */}
                          <TableCell className="text-green-700">{fmt(r.best_price_global)}</TableCell>
                          <TableCell className="text-purple-700">{fmt(r.anchor_global)}</TableCell>
                        </TableRow>
                        );
                      })}
                      {filtered.length === 0 && <TableRow><TableCell colSpan={9 + numBids} className="text-center py-8 text-gray-400 text-sm">No components found</TableCell></TableRow>}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ── DISTRIBUTOR PRICING TAB ── */}
          <TabsContent value="distributors">
            <Card className="bg-white border-gray-200 shadow-sm">
              <CardContent className="p-4 space-y-3">
                {filtered.map((r, idx) => {
                  const supplierEntries = (r.distributor_pricing || []).filter(d => d.source === 'Supplier');
                  const dataEntries = (r.distributor_pricing || []).filter(d => d.source !== 'Supplier');
                  return (
                    <div key={idx} className="border border-gray-200 rounded-lg overflow-hidden" data-testid={`dist-section-${idx}`}>
                      <button className="w-full flex items-center justify-between px-4 py-2.5 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
                        onClick={() => setExpandedMpn(expandedMpn === r.mpn ? null : r.mpn)}>
                        <div className="flex items-center gap-3">
                          <span className="font-mono font-medium text-sm text-gray-900">{r.mpn}</span>
                          <Badge variant="outline" className="text-[10px]">{supplierEntries.length} supplier bid{supplierEntries.length !== 1 ? 's' : ''}</Badge>
                          <Badge variant="outline" className="text-[10px]">{dataEntries.length} 1Data sources</Badge>
                        </div>
                        {expandedMpn === r.mpn ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
                      </button>
                      {expandedMpn === r.mpn && (
                        <div className="border-t border-gray-200">
                          <Table>
                            <TableHeader>
                              <TableRow className="bg-gray-50/60 text-[10px]">
                                <TableHead className="font-semibold">Source</TableHead>
                                <TableHead className="font-semibold">Name</TableHead>
                                <TableHead className="font-semibold">Country</TableHead>
                                <TableHead className="font-semibold">Price</TableHead>
                                <TableHead className="font-semibold">Type</TableHead>
                                <TableHead className="font-semibold">Rate</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {/* Supplier bids first */}
                              {supplierEntries.map((dp, di) => (
                                <TableRow key={`s-${di}`} className="text-xs bg-violet-50/50">
                                  <TableCell><Badge className="bg-violet-100 text-violet-800 text-[9px]">Supplier</Badge></TableCell>
                                  <TableCell className="font-semibold text-violet-800">{dp.distributor_name}</TableCell>
                                  <TableCell className="text-gray-500">{dp.country_of_supply}</TableCell>
                                  <TableCell className="font-semibold text-violet-700">{fmt(dp.price)}</TableCell>
                                  <TableCell><Badge className="bg-violet-100 text-violet-700 text-[9px]">{dp.distributor_type}</Badge></TableCell>
                                  <TableCell><Badge className="bg-violet-100 text-violet-700 text-[9px]">{dp.rate_type}</Badge></TableCell>
                                </TableRow>
                              ))}
                              {/* 1Data sources */}
                              {dataEntries.map((dp, di) => (
                                <TableRow key={`d-${di}`} className="text-xs">
                                  <TableCell><Badge className="bg-blue-100 text-blue-800 text-[9px]">1Data</Badge></TableCell>
                                  <TableCell className="font-medium">{dp.distributor_name}</TableCell>
                                  <TableCell>{dp.country_of_supply}</TableCell>
                                  <TableCell className="font-semibold text-green-700">{fmt(dp.price)}</TableCell>
                                  <TableCell>
                                    <Badge className={`text-[9px] ${dp.distributor_type === 'authorized' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-700'}`}>{dp.distributor_type}</Badge>
                                  </TableCell>
                                  <TableCell>
                                    <Badge className={`text-[9px] ${dp.rate_type === 'Spot' ? 'bg-amber-100 text-amber-800' : 'bg-green-100 text-green-800'}`}>{dp.rate_type}</Badge>
                                  </TableCell>
                                </TableRow>
                              ))}
                              {(r.distributor_pricing || []).length === 0 && (
                                <TableRow><TableCell colSpan={6} className="text-center py-4 text-gray-400 text-xs">No pricing data</TableCell></TableRow>
                              )}
                            </TableBody>
                          </Table>
                        </div>
                      )}
                    </div>
                  );
                })}
                {filtered.length === 0 && <div className="text-center py-8 text-gray-400 text-sm">No components found</div>}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ── AI RECOMMENDATIONS TAB ── */}
          <TabsContent value="ai">
            <Card className="bg-white border-gray-200 shadow-sm">
              <CardContent className="p-5">
                {!recommendations && !recoLoading ? (
                  <div className="text-center py-12 space-y-3">
                    <Sparkles className="h-10 w-10 text-blue-400 mx-auto" />
                    <h4 className="font-semibold text-gray-800">AI Pricing Recommendations</h4>
                    <p className="text-sm text-gray-500 max-w-md mx-auto">Get AI-powered pricing strategy and supplier comparison recommendations.</p>
                    <Button onClick={() => fetchRecommendations()} className="bg-blue-600 hover:bg-blue-700 text-white gap-1.5" data-testid="btn-generate-reco">
                      <Sparkles className="h-4 w-4" /> Generate Recommendations
                    </Button>
                  </div>
                ) : recoLoading ? (
                  <div className="text-center py-12 space-y-3">
                    <Loader2 className="h-8 w-8 text-blue-500 animate-spin mx-auto" />
                    <p className="text-sm text-gray-500">Generating AI recommendations...</p>
                  </div>
                ) : (
                  <div className="space-y-4" data-testid="reco-results">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-semibold text-gray-800">AI Pricing Strategy</h4>
                        <p className="text-sm text-gray-500 mt-0.5">{recommendations.summary}</p>
                      </div>
                      <Button variant="outline" size="sm" className="gap-1 text-xs" onClick={() => fetchRecommendations(true)} data-testid="btn-regenerate-reco">
                        <RefreshCw className="h-3.5 w-3.5" /> Regenerate
                      </Button>
                    </div>
                    <div className="space-y-3">
                      {(recommendations.recommendations || []).map((rec, i) => {
                        const actionLabel = { renegotiate_price: 'Renegotiate Price', request_requote: 'Request Requote', source_alternate_supplier: 'Source Alternate', consolidate_volume: 'Consolidate Volume', approve_award: 'Approve & Award' }[rec.action_type] || rec.action_type?.replace(/_/g, ' ');
                        const actionColor = { renegotiate_price: 'bg-red-50 text-red-700 border-red-200', request_requote: 'bg-amber-50 text-amber-700 border-amber-200', source_alternate_supplier: 'bg-blue-50 text-blue-700 border-blue-200', consolidate_volume: 'bg-purple-50 text-purple-700 border-purple-200', approve_award: 'bg-green-50 text-green-700 border-green-200' }[rec.action_type] || 'bg-gray-50 text-gray-700';
                        return (
                        <div key={i} className={`border rounded-lg p-4 ${priorityColor[rec.priority] || 'border-gray-200'}`} data-testid={`reco-item-${i}`}>
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 flex-wrap mb-1">
                                <Badge className={`text-[10px] ${priorityColor[rec.priority] || 'bg-gray-100 text-gray-700'}`}>{rec.priority}</Badge>
                                <Badge variant="outline" className={`text-[10px] ${actionColor}`}>{actionLabel}</Badge>
                                {rec.estimated_savings && <Badge variant="outline" className="text-[10px] text-green-700 border-green-200">{rec.estimated_savings}</Badge>}
                              </div>
                              <h5 className="font-semibold text-sm text-gray-900">{rec.title}</h5>
                              <p className="text-xs text-gray-600 mt-1">{rec.description}</p>
                              {rec.affected_components?.length > 0 && (
                                <div className="flex flex-wrap gap-1 mt-2">
                                  {rec.affected_components.map((mpn, j) => <Badge key={j} variant="outline" className="text-[9px] font-mono">{mpn}</Badge>)}
                                </div>
                              )}
                            </div>
                            <Button variant="outline" size="sm" className="gap-1 text-[10px] shrink-0" onClick={() => {
                              setAssignCtx({ sourceModule: 'Bid Analyzer', bomName: bid?.bom_name, recommendation: `[${actionLabel}] ${rec.title}: ${rec.description}`, mpn: rec.affected_components?.[0] || '', actionType: rec.action_type || '' });
                              setAssignOpen(true);
                            }} data-testid={`btn-assign-reco-${i}`}>
                              <UserPlus className="h-3 w-3" /> Assign
                            </Button>
                          </div>
                        </div>
                        );
                      })}
                      {(recommendations.recommendations || []).length === 0 && <p className="text-center text-gray-400 text-sm py-6">No recommendations generated.</p>}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
        {assignOpen && <QuickAssignModal open={assignOpen} onClose={() => setAssignOpen(false)} context={assignCtx} />}
      </div>
    </Layout>
  );
};

export default OneBidDetail;
